import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  type InvitationOutcome,
  type InvitationView,
  resendAdminInvitation,
  revokeAdminInvitation,
} from '../lib/admin-invitations';
import { createConfirmGate } from '../lib/confirm-gate';
import { createKeyedMutation } from '../lib/keyed-mutation';
import type { OperatorRefusal } from '../lib/operator-credential';
import { ConfirmDialog } from './ConfirmDialog';
import { OperatorRefusalAlert } from './OperatorRefusalAlert';
import { Button } from './ui/Button';

// The actions cell of one admin invitations row (arc 101 slice 01). Revoke / send-again go
// through the REST twins (`lib/admin-invitations.ts`) on an `admin:users` operator
// credential (#5724): a door refusal renders `<OperatorRefusalAlert/>` and sends
// nothing; the API's own `detail` is surfaced inline on any other refusal. On success `onOutcome` hands the answer UP: the
// panel shows it and re-reads the list, and it has to be the panel, because the
// re-read can unmount this row (a revoked row leaves the `pending` filter) and a
// resend's one-time link would go with it.
//
// BOTH VERBS ARE OFFERED ON A PENDING ROW ONLY. Revoke is terminal and a resend
// rotates the live token; on a settled row (accepted, expired, revoked) each
// could only spend a round trip on a 409. An expired address that still needs
// in gets a fresh mint from the inbox, which is where the bundle lives.
//
// REVOKE ASKS FIRST (plan 101 slice 17 row 17-16, `createConfirmGate`): it is
// terminal, and it sat one click away beside the benign resend.
//
// `status` is the SERVER's answer, resolved against its clock at read; nothing
// here re-derives expiry from `expires_at_iso` and a browser clock.

export function AdminInvitationActions(props: {
  readonly row: InvitationView;
  readonly token: string | null;
  readonly onOutcome: (outcome: InvitationOutcome) => void;
}): JSX.Element {
  const [refusal, setRefusal] = createSignal<OperatorRefusal | null>(null);
  const act = createKeyedMutation({
    fallback: t('admin.write_error'),
    onStart: () => setRefusal(null),
  });
  const busy = (): boolean => act.isBusy('revoke') || act.isBusy('resend');

  // The row the confirm was ASKED about, not whatever `props.row` is when it lands.
  const revoke = (row: InvitationView): Promise<void> =>
    act.run(
      'revoke',
      async () => {
        const out = await revokeAdminInvitation(row.id, props.token);
        if (out.kind !== 'ok') return void setRefusal(out.kind);
        props.onOutcome({ kind: 'revoked', answer: out.value });
      },
      t('admin.invitation_revoke_failed'),
    );
  const asking = createConfirmGate<InvitationView>({ act: revoke });
  const resend = (): Promise<void> =>
    act.run(
      'resend',
      async () => {
        const out = await resendAdminInvitation(props.row.id, props.token);
        if (out.kind !== 'ok') return void setRefusal(out.kind);
        props.onOutcome({ kind: 'resent', answer: out.value });
      },
      t('admin.invitation_resend_failed'),
    );

  return (
    <>
      <div class="admin-row__controls">
        <Show when={props.row.status === 'pending'}>
          <div class="data-table__actions">
            <Button variant="outline" size="sm" disabled={busy()} onClick={() => void resend()}>
              {t('admin.invitation_resend')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={busy()}
              onClick={() => asking.ask(props.row)}
            >
              {t('admin.invitation_revoke')}
            </Button>
          </div>
        </Show>
        <OperatorRefusalAlert refusal={refusal()} />
        <Show when={act.error()}>
          {(msg) => (
            <p class="field__error" role="alert">
              {msg()}
            </p>
          )}
        </Show>
      </div>
      <ConfirmDialog
        open={asking.pending() !== null}
        title={t('admin.invitation_revoke_title', { email: props.row.email })}
        desc={t('admin.invitation_revoke_desc')}
        cancelLabel={t('admin.confirm_cancel')}
        actionLabel={t('admin.invitation_revoke_confirm')}
        destructive
        onCancel={asking.cancel}
        onConfirm={() => void asking.confirm()}
      />
    </>
  );
}
